import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Bell, BellRing, FileText, AlertTriangle, ClipboardList, Volume2, VolumeX, Check } from 'lucide-react';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuTrigger,
} from './ui/dropdown-menu';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Polling interval — 30 s feels reactive without hammering the API.
const POLL_MS = 30000;

// Storage keys
const SEEN_KEY = 'luxy_notif_seen_ids_v1';
const SOUND_KEY = 'luxy_notif_sound_enabled_v1';
const HISTORY_KEY = 'luxy_notif_history_v1';

// Maximum number of notifications kept in the dropdown history.
const HISTORY_LIMIT = 30;

function readJSON(key, fallback) {
    try {
        const v = localStorage.getItem(key);
        return v == null ? fallback : JSON.parse(v);
    } catch { return fallback; }
}
function writeJSON(key, value) {
    try { localStorage.setItem(key, JSON.stringify(value)); } catch { /* ignore quota */ }
}

// Tiny WebAudio beep — no external file required.
function playBeep() {
    try {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        if (!Ctx) return;
        const ctx = new Ctx();
        const o = ctx.createOscillator();
        const g = ctx.createGain();
        o.type = 'sine';
        o.frequency.setValueAtTime(880, ctx.currentTime);
        o.frequency.exponentialRampToValueAtTime(660, ctx.currentTime + 0.18);
        g.gain.setValueAtTime(0.0001, ctx.currentTime);
        g.gain.exponentialRampToValueAtTime(0.25, ctx.currentTime + 0.02);
        g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.35);
        o.connect(g); g.connect(ctx.destination);
        o.start();
        o.stop(ctx.currentTime + 0.4);
        // Auto close to release the audio context
        setTimeout(() => { try { ctx.close(); } catch { /* ignore */ } }, 700);
    } catch { /* never throw from a notification helper */ }
}

const SOURCES = [
    {
        key: 'devis_non_envoyes',
        label: 'Devis non envoyé',
        endpoint: '/api/dashboard/devis-non-envoyes',
        icon: FileText,
        color: 'text-amber-500',
        bg: 'bg-amber-50',
        route: '/dashboard',
        format: (item) => `${item.nom || 'Sans nom'} — devis à envoyer`,
    },
    {
        key: 'rdv_urgents',
        label: 'Urgent',
        endpoint: '/api/dashboard/rdv-non-affectes',
        icon: AlertTriangle,
        color: 'text-red-500',
        bg: 'bg-red-50',
        route: '/dashboard',
        format: (item) => `${item.nom || 'RDV'} — non affecté à un commercial`,
    },
    {
        key: 'tasks',
        label: 'Tâches à effectuer',
        endpoint: '/api/tasks?status=active',
        icon: ClipboardList,
        color: 'text-[#C9A962]',
        bg: 'bg-[#C9A962]/10',
        route: '/taches',
        format: (item) => item.text || 'Nouvelle tâche',
    },
];

export default function NotificationBell() {
    const navigate = useNavigate();
    const [history, setHistory] = useState(() => readJSON(HISTORY_KEY, []));
    const [unreadCount, setUnreadCount] = useState(0);
    const [soundEnabled, setSoundEnabled] = useState(() => readJSON(SOUND_KEY, true));
    const [open, setOpen] = useState(false);
    const [pulse, setPulse] = useState(false);
    const firstRunRef = useRef(true);

    // Persist sound pref
    useEffect(() => { writeJSON(SOUND_KEY, soundEnabled); }, [soundEnabled]);

    const recomputeUnread = useCallback((hist) => {
        setUnreadCount(hist.filter(n => !n.read).length);
    }, []);

    useEffect(() => { recomputeUnread(history); }, [history, recomputeUnread]);

    const poll = useCallback(async () => {
        let seen = readJSON(SEEN_KEY, {});
        if (!seen || typeof seen !== 'object') seen = {};
        const newNotifs = [];

        await Promise.all(SOURCES.map(async (src) => {
            try {
                const res = await axios.get(`${API_URL}${src.endpoint}`, { withCredentials: true });
                const items = Array.isArray(res.data) ? res.data : [];
                const knownIds = new Set(seen[src.key] || []);
                const currentIds = items.map(it => it.id).filter(Boolean);
                // First run: seed the seen list, do NOT raise notifications for
                // items that already exist when the user logs in.
                if (firstRunRef.current) {
                    seen[src.key] = currentIds;
                    return;
                }
                // Otherwise, anything not in known set is a new arrival.
                items.forEach((it) => {
                    if (!it.id || knownIds.has(it.id)) return;
                    newNotifs.push({
                        id: `${src.key}:${it.id}`,
                        sourceKey: src.key,
                        label: src.label,
                        text: src.format(it),
                        route: src.route,
                        ts: new Date().toISOString(),
                        read: false,
                    });
                });
                seen[src.key] = currentIds;
            } catch {
                /* ignore individual source failure — keep the bell working for the others */
            }
        }));

        firstRunRef.current = false;
        writeJSON(SEEN_KEY, seen);

        if (newNotifs.length > 0) {
            setHistory((prev) => {
                // De-dupe by id to be safe (rapid double polls)
                const existingIds = new Set(prev.map(n => n.id));
                const additions = newNotifs.filter(n => !existingIds.has(n.id));
                if (additions.length === 0) return prev;
                const next = [...additions, ...prev].slice(0, HISTORY_LIMIT);
                writeJSON(HISTORY_KEY, next);
                return next;
            });
            setPulse(true);
            setTimeout(() => setPulse(false), 1500);
            if (soundEnabled) playBeep();
        }
    }, [soundEnabled]);

    // Initial poll + interval
    useEffect(() => {
        poll();
        const id = setInterval(poll, POLL_MS);
        return () => clearInterval(id);
    }, [poll]);

    const markAllRead = () => {
        setHistory((prev) => {
            const next = prev.map(n => ({ ...n, read: true }));
            writeJSON(HISTORY_KEY, next);
            return next;
        });
    };

    const markRead = (id) => {
        setHistory((prev) => {
            const next = prev.map(n => n.id === id ? { ...n, read: true } : n);
            writeJSON(HISTORY_KEY, next);
            return next;
        });
    };

    const handleClickNotif = (n) => {
        markRead(n.id);
        setOpen(false);
        if (n.route) navigate(n.route);
    };

    const clearAll = () => {
        setHistory([]);
        writeJSON(HISTORY_KEY, []);
    };

    const formatTime = (iso) => {
        try {
            const d = new Date(iso);
            const diff = Math.floor((Date.now() - d.getTime()) / 1000);
            if (diff < 60) return "à l'instant";
            if (diff < 3600) return `il y a ${Math.floor(diff / 60)} min`;
            if (diff < 86400) return `il y a ${Math.floor(diff / 3600)} h`;
            return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
        } catch { return ''; }
    };

    const Icon = pulse ? BellRing : Bell;

    return (
        <DropdownMenu open={open} onOpenChange={setOpen}>
            <DropdownMenuTrigger asChild>
                <button
                    type="button"
                    className={`relative inline-flex items-center justify-center h-9 w-9 rounded-full transition-colors hover:bg-gray-100 ${pulse ? 'animate-pulse' : ''}`}
                    data-testid="notif-bell"
                    aria-label="Notifications"
                >
                    <Icon className={`w-5 h-5 ${unreadCount > 0 ? 'text-[#C9A962]' : 'text-gray-500'}`} />
                    {unreadCount > 0 && (
                        <span
                            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center"
                            data-testid="notif-badge"
                        >
                            {unreadCount > 99 ? '99+' : unreadCount}
                        </span>
                    )}
                </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-80 p-0" data-testid="notif-panel">
                {/* Header */}
                <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
                    <h3 className="text-sm font-semibold text-[#1B365D]">Notifications</h3>
                    <div className="flex items-center gap-1">
                        <button
                            onClick={() => setSoundEnabled(s => !s)}
                            className="p-1.5 rounded-md hover:bg-gray-100 text-gray-500"
                            title={soundEnabled ? 'Désactiver le son' : 'Activer le son'}
                            data-testid="notif-toggle-sound"
                        >
                            {soundEnabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
                        </button>
                        {unreadCount > 0 && (
                            <button
                                onClick={markAllRead}
                                className="p-1.5 rounded-md hover:bg-gray-100 text-gray-500"
                                title="Tout marquer comme lu"
                                data-testid="notif-mark-all-read"
                            >
                                <Check className="w-4 h-4" />
                            </button>
                        )}
                    </div>
                </div>
                {/* List */}
                <div className="max-h-96 overflow-y-auto" data-testid="notif-list">
                    {history.length === 0 ? (
                        <div className="px-4 py-10 text-center text-sm text-gray-400">
                            Aucune notification pour le moment.
                        </div>
                    ) : (
                        history.map((n) => {
                            const src = SOURCES.find(s => s.key === n.sourceKey);
                            const SrcIcon = src?.icon || Bell;
                            return (
                                <button
                                    key={n.id}
                                    onClick={() => handleClickNotif(n)}
                                    className={`w-full flex items-start gap-3 px-4 py-3 text-left hover:bg-gray-50 transition-colors border-b border-gray-50 ${n.read ? 'opacity-60' : ''}`}
                                    data-testid={`notif-item-${n.id}`}
                                >
                                    <div className={`shrink-0 w-9 h-9 rounded-full flex items-center justify-center ${src?.bg || 'bg-gray-100'}`}>
                                        <SrcIcon className={`w-4 h-4 ${src?.color || 'text-gray-500'}`} />
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{n.label}</p>
                                        <p className="text-sm text-gray-800 mt-0.5 truncate">{n.text}</p>
                                        <p className="text-[11px] text-gray-400 mt-1">{formatTime(n.ts)}</p>
                                    </div>
                                    {!n.read && <span className="shrink-0 w-2 h-2 rounded-full bg-[#C9A962] mt-2" />}
                                </button>
                            );
                        })
                    )}
                </div>
                {history.length > 0 && (
                    <div className="px-4 py-2 border-t border-gray-100 text-center">
                        <button
                            onClick={clearAll}
                            className="text-xs text-gray-500 hover:text-red-500"
                            data-testid="notif-clear-all"
                        >
                            Effacer l'historique
                        </button>
                    </div>
                )}
            </DropdownMenuContent>
        </DropdownMenu>
    );
}
